'use client'

import React, { createContext, useCallback, useContext, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

// Acceso PRO. Antes esto era `localStorage['cs_pro_license']` validado por el
// shell contra las download keys de itch.io — o sea: cualquiera con la consola
// del navegador abierta era PRO, y desde la web no se podía activar nunca.
//
// Ahora el acceso lo resuelve el server (server/entitlements.js) y llega junto
// con la sesión en /api/auth/me. Este contexto ya no decide nada: solo expone
// lo que dice AuthContext y el canje de claves.

const ProContext = createContext({
    isPro: false,
    hasFeature: () => false,
    isValidating: false,
    activateError: null,
    activateLicense: async () => false,
})

// El server manda un código de motivo; el texto lo pone la UI, que sabe en qué
// idioma está. Si aparece uno que esta versión no conoce, se usa el mensaje que
// vino del server (castellano) en vez de dejar al usuario sin explicación.
const MOTIVOS = {
    not_found: 'BUY_KEY_ERR_NOT_FOUND',
    revoked: 'BUY_KEY_ERR_REVOKED',
    exhausted: 'BUY_KEY_ERR_EXHAUSTED',
    already_redeemed: 'BUY_KEY_ERR_ALREADY_REDEEMED',
    already_pro: 'BUY_KEY_ERR_ALREADY_PRO',
}

export function ProProvider({ children }) {
    const { tier, hasFeature, applyEntitlement, refresh } = useAuth()
    const { t } = useLanguage()
    const [isValidating, setIsValidating] = useState(false)
    const [activateError, setActivateError] = useState(null)

    const activateLicense = useCallback(
        async (key) => {
            const code = key?.trim()
            if (!code) {
                setActivateError('Ingresá una key válida.')
                return false
            }

            setIsValidating(true)
            setActivateError(null)
            try {
                const res = await fetch('/api/keys/redeem', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ code }),
                })
                const data = await res.json().catch(() => ({}))

                if (!res.ok) {
                    const clave = MOTIVOS[data?.reason]
                    setActivateError(clave ? t(clave) : (data?.error ?? 'No se pudo canjear la clave.'))
                    return false
                }

                // El endpoint ya devuelve el acceso recalculado, así que no hace
                // falta volver a pedir /api/auth/me para que se prenda la UI.
                applyEntitlement(data)
                return true
            } catch {
                setActivateError('No se pudo conectar con el servidor.')
                return false
            } finally {
                setIsValidating(false)
            }
        },
        [applyEntitlement, t],
    )

    const value = {
        isPro: tier === 'pro',
        tier,
        hasFeature,
        isValidating,
        activateError,
        activateLicense,
        // Para después de vincular/desvincular Patreon.
        refreshAccess: refresh,
    }

    return <ProContext.Provider value={value}>{children}</ProContext.Provider>
}

export function usePro() {
    return useContext(ProContext)
}
